import { addFlowStepPrefix } from "common/utils";
import { Flow, FlowData, FlowDataMap, FlowStep } from "core/api";
import { FieldValues } from "react-hook-form";
import { stepDataResolver } from "./steps";

/**
 * Gets current step config
 * @param flow Current flow
 * @param currentStepId Current step id
 * @returns Current step config
 */
export function getCurrentStep(
  flow: Flow | undefined,
  currentStepId?: string
): FlowStep | undefined {
  if (!flow) {
    return undefined;
  }
  return flow.steps.find(
    ({ id }) => id === (currentStepId ?? flow.currentStepId)
  );
}

/**
 * Gets current sub step config
 * @param flow Current flow
 * @param currentStepId Current step id
 * @param currentSubStepId Current sub step id
 * @returns Current sub step config
 */
export function getCurrentSubStep(
  flow: Flow | undefined,
  currentStepId?: string,
  currentSubStepId?: string
): FlowStep | undefined {
  const currentStep = getCurrentStep(flow, currentStepId);
  if (!flow || currentStep?.uiComponent !== "SelectStep") {
    return undefined;
  }
  const currentStepData = flow.data[currentStep.id];
  const _currentSubStepId =
    currentStepData.uiComponent === "SelectStep"
      ? currentStepData.selectedStepId
      : undefined;
  if (!_currentSubStepId) {
    return undefined;
  }
  return currentStep.config.steps.find(
    (step) => step.id === (currentSubStepId ?? _currentSubStepId)
  );
}

/**
 * Gets data for current/target step
 * @param flow Current flow
 * @param currentStepId Current step id
 * @returns Flow step data
 */
export function getCurrentData(
  flow: Flow | undefined,
  currentStepId?: string
): FlowData | undefined {
  if (!flow) {
    return undefined;
  }
  return flow.data[currentStepId ?? flow.currentStepId];
}

/**
 * Creates new flow data with inserted field values
 * @param flow Flow data
 * @param fieldValues Field values
 * @param currentStepId Current step id
 * @returns Flow data with inserted field values
 */
export function insertStepValues(
  flow: Flow,
  fieldValues: FieldValues,
  currentStepId: string | undefined
): Flow {
  // Flatten list of steps to include sub steps
  const steps = flow.steps.flatMap((step) =>
    "steps" in step.config ? [step, ...(step.config.steps ?? [])] : [step]
  );

  const flowStep = steps.find((step) => step.id === currentStepId);
  const stepData = flow.data[currentStepId || ""];

  // Must also ensure resolver to the selected step is applied.
  const selectedStepId =
    flowStep?.uiComponent === "SelectStep"
      ? fieldValues[addFlowStepPrefix(flowStep, "selectedStepId")]
      : undefined;

  const _flow = selectedStepId
    ? { ...insertStepValues(flow, fieldValues, selectedStepId) }
    : flow;

  if (currentStepId && flowStep && stepData) {
    const data = {
      ..._flow.data,
      [currentStepId]: {
        ...stepData,
        // Only updates returned values from resolver
        ...stepDataResolver({
          flow,
          flowStep,
          data: stepData,
          fieldValues,
        }),
      },
    } as FlowDataMap;

    return {
      ..._flow,
      currentStepId: currentStepId ?? flow.currentStepId,
      data,
    };
  } else {
    return flow;
  }
}

/**
 * Group an array on specified key.
 *
 * @param arr The array of the items to group
 * @param keyExtractor the key extractor function to get the group by key.
 * @returns
 *
 * @example  const grouped = groupBy(flow.steps, (item) => item.group || "");
 *
 */
export function groupBy<T, K extends keyof any>(
  arr: T[],
  keyExtractor: (i: T) => K
): Record<K, T[]> {
  return arr.reduce((groups, item) => {
    (groups[keyExtractor(item)] ||= []).push(item);
    return groups;
  }, {} as Record<K, T[]>);
}
